import React from "react";
import { Box, Typography, Container, Grid, Card, CardContent } from "@mui/material";
import { motion } from "framer-motion";
import { FaRocket, FaEye, FaUsers, FaLightbulb, FaHandshake, FaGraduationCap } from "react-icons/fa";
import Bg from "../Images/about.jpg";

const About = () => {
  const darkGlassStyle = {
    background: "rgba(0, 0, 36, 0.85)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(0, 217, 255, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
  };

  const values = [
    {
      icon: <FaLightbulb size={35} />,
      title: "Innovation",
      desc: "Pushing boundaries and embracing new technologies",
    },
    {
      icon: <FaHandshake size={35} />,
      title: "Collaboration",
      desc: "Working together to achieve greater results",
    },
    {
      icon: <FaGraduationCap size={35} />,
      title: "Excellence",
      desc: "Striving for the highest standards in everything",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, rgba(0, 0, 36, 0.95) 0%, rgba(10, 25, 47, 0.9) 50%, rgba(0, 0, 36, 0.95) 100%), url(${Bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        color: "white",
        pt: { xs: 12, md: 14 },
        pb: 8,
      }}
    >
      {/* Hero Section */}
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Typography
            variant="h2"
            sx={{
              textAlign: "center",
              fontWeight: 800,
              mb: 2,
              background: "linear-gradient(90deg, #ffffff, #00d9ff)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            About Us
          </Typography>
          <Typography
            variant="h6"
            sx={{
              textAlign: "center",
              color: "rgba(255, 255, 255, 0.7)",
              mb: 6,
              maxWidth: 600,
              mx: "auto",
            }}
          >
            Empowering the next generation of tech innovators in Zambia
          </Typography>
        </motion.div>

        {/* Main About Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Card
            sx={{
              borderRadius: "25px",
              ...darkGlassStyle,
              mb: 6,
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "15px",
                    background: "linear-gradient(135deg, #00d9ff, #00b4d8)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaUsers size={30} color="white" />
                </Box>
                <Typography variant="h5" sx={{background: "linear-gradient(90deg, #ffffff, #00d9ff)",}} fontWeight={700}>
                  Who We Are
                </Typography>
              </Box>
              <Typography
                variant="body1"
                sx={{
                  color: "rgba(255, 255, 255, 0.8)",
                  lineHeight: 1.9,
                  fontSize: "1.1rem",
                }}
              >
                The ICTAZ NIPA Chapter is a dynamic community dedicated to fostering
                innovation, technological advancement, and professional development
                among students. Our goal is to empower aspiring tech professionals by
                offering learning opportunities, mentorship programs, networking
                events, and hands-on projects that prepare students for success in
                the ever-evolving tech industry.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "rgba(255, 255, 255, 0.8)",
                  lineHeight: 1.9,
                  fontSize: "1.1rem",
                  mt: 2,
                }}
              >
                We strive to bridge the gap between academic learning and real-world
                application by creating an environment where students can
                collaborate, innovate, and engage with industry experts. Through our
                initiatives, we inspire a culture of creativity, problem-solving,
                and leadership within the field of technology.
              </Typography>
            </CardContent>
          </Card>
        </motion.div>

        {/* Mission & Vision */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Card
                sx={{
                  height: "100%",
                  borderRadius: "25px",
                  ...darkGlassStyle,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    border: "1px solid rgba(0, 217, 255, 0.5)",
                    boxShadow: "0 15px 40px rgba(0, 217, 255, 0.2)",
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                    <motion.div
                      animate={{ rotate: [0, 10, -10, 0] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: "50%",
                          background: "linear-gradient(135deg, #00d9ff30, #00d9ff10)",
                          border: "2px solid #00d9ff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#00d9ff",
                        }}
                      >
                        <FaRocket size={25} />
                      </Box>
                    </motion.div>
                    <Typography variant="h5" fontWeight={700} sx={{ color: 'white' }}>
                      Our Mission
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "rgba(255, 255, 255, 0.8)",
                      lineHeight: 1.8,
                    }}
                  >
                    Our mission is to bridge the gap between students and the tech
                    industry by providing cutting-edge resources, hands-on
                    experiences, and mentorship programs. We believe in learning by
                    doing, which is why we focus on real-world projects, hackathons,
                    tech workshops, and industry-led training. By fostering
                    collaborations with leading tech companies and experts, we equip
                    students with the skills, confidence, and connections they need
                    to thrive in today's competitive digital landscape.
                  </Typography>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Card
                sx={{
                  height: "100%",
                  borderRadius: "25px",
                  ...darkGlassStyle,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    border: "1px solid rgba(0, 217, 255, 0.5)",
                    boxShadow: "0 15px 40px rgba(0, 217, 255, 0.2)",
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                    <motion.div
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: "50%",
                          background: "linear-gradient(135deg, #00d9ff30, #00d9ff10)",
                          border: "2px solid #00d9ff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#00d9ff",
                        }}
                      >
                        <FaEye size={25} />
                      </Box>
                    </motion.div>
                    <Typography variant="h5" fontWeight={700} sx={{ color: 'white' }}>
                      Our Vision
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "rgba(255, 255, 255, 0.8)",
                      lineHeight: 1.8,
                    }}
                  >
                    Our vision is to create a thriving ecosystem of tech enthusiasts
                    who are driven by passion, curiosity, and a commitment to
                    innovation. We aspire to be a leading student tech community that
                    shapes the future of technology in Zambia and beyond by producing
                    skilled professionals, influential leaders, and innovative
                    problem-solvers. Through knowledge-sharing, teamwork, and
                    forward-thinking solutions, we aim to contribute meaningfully to
                    the digital transformation and technological progress of our
                    society.
                  </Typography>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>

        {/* Core Values */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h4"
            sx={{
              textAlign: "center",
              fontWeight: 700,
              mb: 4,
            }}
          >
            Our Core <span style={{ color: "#00d9ff" }}>Values</span>
          </Typography>
        </motion.div>

        <Grid container spacing={3}>
          {values.map((value, index) => (
            <Grid item xs={12} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
              >
                <Card
                  sx={{
                    textAlign: "center",
                    borderRadius: "20px",
                    background: "rgba(255, 255, 255, 0.05)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      border: "1px solid rgba(0, 217, 255, 0.3)",
                      boxShadow: "0 15px 40px rgba(0, 217, 255, 0.15)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <motion.div
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: 70,
                          height: 70,
                          borderRadius: "20px",
                          background: "linear-gradient(135deg, #00d9ff30, #00d9ff10)",
                          border: "2px solid #00d9ff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mx: "auto",
                          mb: 2,
                          color: "#00d9ff",
                        }}
                      >
                        {value.icon}
                      </Box>
                    </motion.div>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: 'white' }}>
                      {value.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                    >
                      {value.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default About;
